import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, Share2, Globe, Mail, AlertTriangle } from 'lucide-react';

const PrivacyPage = () => {
  const sections = [
    {
      icon: Shield,
      title: "1. Introduction",
      content: `Crafters Market ("we," "us," or "our") is committed to protecting your privacy and handling your personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).

This Privacy Policy explains:
• What personal information we collect
• Why we collect it
• How we use and store it
• Who we may disclose it to
• Your rights regarding your personal information

By using Crafters Market, you consent to the collection, use, and disclosure of your personal information as described in this policy.`
    },
    {
      icon: Database,
      title: "2. Information We Collect",
      content: `We collect the following types of personal information:

FOR ALL USERS:
• Full name
• Email address
• Phone number
• Account credentials (password stored securely hashed)
• Profile information you choose to provide
• Communication records (messages, support tickets)

FOR SELLERS (Additional Information):
• Residential or business address
• Australian Business Number (ABN) - if a business seller
• Business name - if a business seller
• Bank/payment details (processed securely by Stripe, not stored by us)
• Listing content and images
• Sales and transaction history

FOR BUYERS:
• Shipping addresses
• Purchase history
• Payment information (processed by Stripe)

AUTOMATICALLY COLLECTED:
• IP address and device information
• Browser type and version
• Pages visited and time spent
• Cookies and similar technologies`
    },
    {
      icon: Eye,
      title: "3. Why We Collect This Information (APP 3)",
      content: `Under Australian Privacy Principle 3, we only collect information that is "reasonably necessary" for our business functions. We collect personal information for the following purposes:

PRIMARY PURPOSES:
• Operating and maintaining the marketplace platform
• Facilitating transactions between buyers and sellers
• Processing payments and subscriptions
• Providing customer support
• Sending transaction-related communications

LEGAL & COMPLIANCE PURPOSES:
• Protecting the platform from fraudulent or illegal activity
• Investigating complaints, disputes, and policy violations
• Complying with legal obligations and court orders
• Responding to requests from regulatory authorities (e.g., ACCC)
• Protecting our legal interests and those of our users

SECURITY & ACCOUNTABILITY:
• Verifying seller identity for buyer protection
• Enabling contact between parties in the event of disputes
• Maintaining records as required by consumer protection laws
• Providing information to authorities or courts if legally required

We do NOT sell your personal information to third parties for marketing purposes.`
    },
    {
      icon: Share2,
      title: "4. Who We Disclose Information To (APP 6)",
      content: `We may disclose your personal information to:

TRANSACTION PARTIES:
• Buyers may see seller's business name, general location, and contact methods enabled by the seller
• Sellers receive buyer's name, email, and shipping address to fulfill orders

SERVICE PROVIDERS:
• Stripe (payment processing)
• Email service providers (transactional emails)
• Cloud hosting providers (data storage)
• Analytics services (aggregated, anonymized data)

All service providers are bound by confidentiality obligations.

LEGAL & REGULATORY DISCLOSURE:
We may disclose your information if required or permitted by law to:
• Courts, tribunals, or legal proceedings
• Regulatory authorities (ACCC, Fair Trading, ATO)
• Law enforcement agencies
• Legal representatives in the event of disputes

DISPUTE SITUATIONS:
If a dispute arises between a buyer and seller, we may disclose relevant contact information (name, email, phone, address) to:
• The other party to facilitate resolution
• Consumer protection agencies investigating complaints
• Courts or tribunals handling the dispute
• Legal representatives of either party

SELLER INFORMATION VISIBILITY:
• Your business name and ABN (if applicable) will be publicly displayed on your profile
• Your full address is NOT publicly displayed but may be disclosed in legal proceedings
• Your email and phone may be shared with buyers for transaction purposes`
    },
    {
      icon: Lock,
      title: "5. Data Security (APP 11)",
      content: `We take the security of your personal information seriously and implement appropriate measures to protect it:

TECHNICAL SECURITY:
• Encryption of data in transit (HTTPS/TLS)
• Encryption of sensitive data at rest
• Secure password hashing (bcrypt)
• Regular security updates and patches
• Firewall and intrusion detection systems

ACCESS CONTROLS:
• Role-based access limiting who can view personal information
• Authentication required for all data access
• Audit logs of data access and changes
• Staff training on privacy and security obligations

DATA STORAGE:
• Data stored on secure cloud infrastructure
• Regular encrypted backups
• Data centers with physical security controls
• Geographic redundancy for disaster recovery

INCIDENT RESPONSE:
In the event of a data breach that is likely to result in serious harm, we will:
• Notify affected individuals as soon as practicable
• Report to the Office of the Australian Information Commissioner (OAIC)
• Take immediate steps to contain the breach
• Conduct a thorough investigation

While we implement strong security measures, no system is completely secure. We cannot guarantee absolute security of your information.`
    },
    {
      icon: UserCheck,
      title: "6. Your Rights (APP 12 & 13)",
      content: `You have the following rights regarding your personal information:

ACCESS (APP 12):
• You can request access to the personal information we hold about you
• We will respond to access requests within 30 days
• Access may be refused in limited circumstances (e.g., legal proceedings)

CORRECTION (APP 13):
• You can request correction of inaccurate or outdated information
• You can update most information directly in your account settings
• We will respond to correction requests within 30 days

DELETION:
• You can request deletion of your account and associated data
• Some information may be retained as required by law or for legitimate business purposes
• Transaction records may be retained for tax and legal compliance

OPTING OUT:
• You can opt out of marketing communications at any time
• You cannot opt out of essential transaction-related communications

To exercise these rights, contact us at privacy@craftersmarket.com.au`
    },
    {
      icon: Globe,
      title: "7. Cookies & Tracking",
      content: `We use cookies and similar technologies to:

ESSENTIAL COOKIES:
• Maintain your login session
• Remember your preferences
• Ensure security of your account

ANALYTICS COOKIES:
• Understand how users interact with our platform
• Improve our services and user experience
• Aggregated data only - not individual tracking

You can control cookies through your browser settings. Disabling cookies may affect some platform functionality.

We do NOT:
• Sell your browsing data to advertisers
• Track you across other websites
• Use your data for targeted advertising by third parties`
    },
    {
      icon: AlertTriangle,
      title: "8. Children's Privacy",
      content: `Crafters Market is not intended for use by children under 18 years of age. We do not knowingly collect personal information from children.

If we become aware that we have collected personal information from a child under 18, we will take steps to delete that information as soon as possible.

If you believe a child has provided us with personal information, please contact us immediately at privacy@craftersmarket.com.au`
    },
    {
      icon: Mail,
      title: "9. Contact Us & Complaints",
      content: `For privacy-related inquiries, contact:

Privacy Officer
Crafters Market
Email: privacy@craftersmarket.com.au

COMPLAINTS:
If you believe we have breached the Australian Privacy Principles, you can:
1. Contact us first - we will investigate and respond within 30 days
2. If unsatisfied, lodge a complaint with the Office of the Australian Information Commissioner (OAIC)

OAIC Contact:
Website: www.oaic.gov.au
Phone: 1300 363 992
Email: enquiries@oaic.gov.au`
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Shield className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Privacy Policy
            </h1>
            <p className="text-xl text-white/90">
              How we collect, use, and protect your information
            </p>
            <p className="text-sm text-white/70 mt-2">
              Last updated: March 2026
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 mb-8"
        >
          <p className="text-stone-600 leading-relaxed">
            At Crafters Market, we are committed to protecting your privacy and handling your personal 
            information responsibly. This Privacy Policy complies with the Privacy Act 1988 (Cth) and 
            the Australian Privacy Principles (APPs). Please read this policy carefully to understand 
            how we collect, use, and protect your information.
          </p>
        </motion.div>

        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-white rounded-xl shadow-sm border border-stone-200 p-6"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gradient-to-br from-sky-100 to-teal-100 rounded-lg flex-shrink-0">
                  <section.icon className="h-6 w-6 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {section.title}
                  </h2>
                  <div className="text-stone-600 whitespace-pre-line leading-relaxed text-sm">
                    {section.content}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="bg-gradient-to-r from-sky-50 to-teal-50 rounded-xl p-8 mt-8 text-center"
        >
          <h3 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Questions About Your Privacy?
          </h3>
          <p className="text-stone-600 mb-4">
            If you have any questions about this Privacy Policy or how we handle your data, please contact us.
          </p>
          <a 
            href="mailto:privacy@craftersmarket.com.au" 
            className="text-teal-600 hover:text-teal-700 font-medium"
          >
            privacy@craftersmarket.com.au
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
